import {Alert, Form, FormSubmitHandler, Separator, showNotification} from 'platform/components';
import {Grid, Show, Space, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useEffect, useState} from 'react';

import {always, defaultTo, head, isNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty, isTrue} from 'ramda-adjunct';

import {GetManufacturersResponse, GetSeriesListApiResponse} from '@dms/api/core';
import {BaseAuthorizationProfile, useLazyGetAuthorizationProfileQuery} from '@dms/api/security';
import {
  GetBranchListApiResponse,
  GetCurrenciesApiResponse,
  TenantResponseBody,
} from '@dms/api/shared';
import {
  BaseServiceOrderVariant,
  BaseSupplier,
  GetCodeListOptionsResponse,
  GetCodeListResponse,
  GetDirectSaleVariantsResponse,
  GetWarehouseResponse,
  usePatchWarehouseMutation,
  usePostWarehouseMutation,
  WarehousePricingEnum,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter} from '@dms/settings/shared';
import {handleApiError, useDuplicateErrorHandler} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {
  WarehouseDetailForm as WarehouseDetailFormType,
  WarehouseDetailFormSchema,
} from '../types/WarehouseDetailForm';
import {AccountingForm} from './AccountingForm';
import {DocumentsForm} from './DocumentsForm';
import {GeneralForm} from './GeneralForm';
import {PricingForm} from './PricingForm';
import {SuppliersForm} from './SuppliersForm';

export interface WarehouseDetailFormProps extends RequiredTestIdProps {
  tenant: TenantResponseBody | Nullish;
  warehouse: GetWarehouseResponse | Nullish;
  authorizationProfiles: BaseAuthorizationProfile[] | Nullish;
  branches: GetBranchListApiResponse | Nullish;
  manufacturers: GetManufacturersResponse | Nullish;
  warehousePricing: GetCodeListResponse | Nullish;
  currencies: GetCurrenciesApiResponse | Nullish;
  salesPriceCalculationOptions: GetCodeListOptionsResponse | Nullish;
  baseSourcePriceOptions: GetCodeListOptionsResponse | Nullish;
  series: GetSeriesListApiResponse | Nullish;
  serviceOrderVariants: BaseServiceOrderVariant[] | Nullish;
  directSaleVariants: GetDirectSaleVariantsResponse | Nullish;
  suppliers: BaseSupplier[] | Nullish;
  isEditDisabled: boolean;
}

export function WarehouseDetailForm(props: WarehouseDetailFormProps) {
  const navigate = useNavigate();

  const {duplicateError: isDuplicate, duplicateErrorHandler} = useDuplicateErrorHandler();

  const [selectedAuthorizationProfile, setSelectedAuthorizationProfile] = useState<
    BaseAuthorizationProfile | Nullish
  >(null);

  const [postWarehouse, {isLoading: isCreatingWarehouse}] = usePostWarehouseMutation();

  const [patchWarehouse, {isLoading: isPatchingWarehouse}] = usePatchWarehouseMutation();

  const [getAuthorizationProfile, {isFetching: isAuthorizationProfileFetching}] =
    useLazyGetAuthorizationProfileQuery();

  useEffect(() => {
    if (isNil(props.warehouse?.authorizationProfileId) || isNil(props.tenant?.id)) {
      return;
    }

    getAuthorizationProfile({
      authorizationProfileId: props.warehouse.authorizationProfileId,
      'x-tenant': props.tenant.id,
    })
      .unwrap()
      .then(setSelectedAuthorizationProfile)
      .catch(handleApiError);
  }, [props.warehouse?.authorizationProfileId, props.tenant?.id, getAuthorizationProfile]);

  const handleSubmit: FormSubmitHandler<WarehouseDetailFormType> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const isAllSuppliersEnabled = isTrue(data.allSuppliers);
    const salesPriceCalculation = data.salesPriceCalculation;

    const body = {
      // general
      name: data.name,
      warehouseNumber: data.warehouseNumber,
      authorizationProfileId: data.authorizationProfileId,
      companyBillingInfoId: data.companyBillingInfoId,
      branchId: data.branchId,
      supportedMake: data.supportedMake,
      defaultManufacturerId: data.defaultManufacturerId,
      // accounting
      warehousePricing: head(data.warehousePricing) as WarehousePricingEnum,
      isFastTrack: data.isFastTrack,
      dummyWarehouse: data.dummyWarehouse,
      workItemGroupId: data.workItemGroupId,
      // pricing
      commercialOrderMarginMinimum: data.commercialOrderMarginMinimum,
      salesPriceCalculation: data.salesPriceCalculation,
      salePriceMarginPercent: data.salePriceMarginPercent,
      salePriceMarkupPercent: data.salePriceMarkupPercent,
      salePriceProfitWithoutVat: data.salePriceProfitWithoutVat,
      basePriceSource: data.basePriceSource,
      unitSalesPriceWithVat: data.unitSalesPriceWithVat,
      blockPricingSettingOnArticle: data.blockPricingSettingOnArticle,
      // documents
      receiveNoteSeriesId: data.receiveNoteSeriesId,
      receiveNoteTemplateId: data.receiveNoteTemplateId,
      creditNoteSeriesId: data.creditNoteSeriesId,
      creditNoteTemplateId: data.creditNoteTemplateId,
      issueNoteSeriesId: data.issueNoteSeriesId,
      issueNoteTemplateId: data.issueNoteTemplateId,
      issueNoteCorrectionSeriesId: data.issueNoteCorrectionSeriesId,
      issueNoteCorrectionTemplateId: data.issueNoteCorrectionTemplateId,
      allowServiceOrderIssue: data.allowServiceOrderIssue,
      allowedOrderVariants:
        isTrue(data.allowServiceOrderIssue) && isNilOrEmpty(data.allowedOrderVariants)
          ? []
          : data.allowedOrderVariants,
      allowDirectSaleIssue: data.allowDirectSaleIssue,
      allowedDirectSaleVariants:
        isTrue(data.allowDirectSaleIssue) && isNilOrEmpty(data.allowedDirectSaleVariants)
          ? []
          : data.allowedDirectSaleVariants,
      // suppliers
      allSuppliers: data.allSuppliers,
      suppliers: isAllSuppliersEnabled
        ? []
        : (data.suppliers?.filter((supplier) => isNotNilOrEmpty(supplier.id)) ?? []),
      defaultSupplierId: data.defaultSupplierId as string,
    };

    // We need to clear previously set sale price values and send just the one of selected type of price
    const requestBody = match(salesPriceCalculation)
      .with(
        'MARGIN_ON_BASE',
        always({
          ...body,
          salePriceMarkupPercent: null,
          salePriceProfitWithoutVat: null,
        })
      )
      .with(
        'MARKUP_ON_BASE',
        always({
          ...body,
          salePriceMarginPercent: null,
          salePriceProfitWithoutVat: null,
        })
      )
      .with(
        'PROFIT_ON_BASE',
        always({...body, salePriceMarginPercent: null, salePriceMarkupPercent: null})
      )
      .otherwise(
        always({
          ...body,
          salePriceMarginPercent: null,
          salePriceMarkupPercent: null,
          salePriceProfitWithoutVat: null,
        })
      );

    const mutation = props.warehouse?.id
      ? patchWarehouse({body: requestBody, warehouseId: props.warehouse.id})
      : postWarehouse(requestBody);

    await mutation
      .unwrap()
      // We need to explicitely set the success message, otherwise it's not testable in jest
      .then(() => showNotification.success(i18n.t('general.notifications.success')))
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.warehousesList);
      })
      .catch(duplicateErrorHandler);
  };

  const defaultValues: Partial<WarehouseDetailFormType> = {
    // general
    name: props.warehouse?.name,
    warehouseNumber: props.warehouse?.warehouseNumber,
    authorizationProfileId: props.warehouse?.authorizationProfileId,
    companyBillingInfoId: props.warehouse?.companyBillingInfoId,
    branchId: defaultTo(props.warehouse?.branchId, selectedAuthorizationProfile?.branchId),
    supportedMake: props.warehouse?.supportedMake,
    defaultManufacturerId: props.warehouse?.defaultManufacturerId,
    // accounting
    warehousePricing: [
      defaultTo(
        props.warehousePricing?.[0].code,
        props.warehouse?.warehousePricing
      ) as WarehousePricingEnum,
    ],
    currency: defaultTo(props.tenant?.currency, props.warehouse?.currency),
    isFastTrack: defaultTo(false, props.warehouse?.isFastTrack),
    dummyWarehouse: defaultTo(false, props.warehouse?.dummyWarehouse),
    workItemGroupId: props.warehouse?.workItemGroupId,
    // pricing
    commercialOrderMarginMinimum: props.warehouse?.commercialOrderMarginMinimum,
    salesPriceCalculation: props.warehouse?.salesPriceCalculation,
    salePriceMarginPercent: props.warehouse?.salePriceMarginPercent,
    salePriceMarkupPercent: props.warehouse?.salePriceMarkupPercent,
    salePriceProfitWithoutVat: props.warehouse?.salePriceProfitWithoutVat,
    basePriceSource: props.warehouse?.basePriceSource,
    unitSalesPriceWithVat: defaultTo(false, props.warehouse?.unitSalesPriceWithVat),
    blockPricingSettingOnArticle: defaultTo(false, props.warehouse?.blockPricingSettingOnArticle),
    // documents
    receiveNoteSeriesId: props.warehouse?.receiveNoteSeriesId,
    receiveNoteTemplateId: props.warehouse?.receiveNoteTemplateId,
    creditNoteSeriesId: props.warehouse?.creditNoteSeriesId,
    creditNoteTemplateId: props.warehouse?.creditNoteTemplateId,
    issueNoteSeriesId: props.warehouse?.issueNoteSeriesId,
    issueNoteTemplateId: props.warehouse?.issueNoteTemplateId,
    issueNoteCorrectionSeriesId: props.warehouse?.issueNoteCorrectionSeriesId,
    issueNoteCorrectionTemplateId: props.warehouse?.issueNoteCorrectionTemplateId,
    allowServiceOrderIssue: defaultTo(true, props.warehouse?.allowServiceOrderIssue),
    allowedOrderVariants: props.warehouse?.allowedOrderVariants,
    allowDirectSaleIssue: defaultTo(true, props.warehouse?.allowDirectSaleIssue),
    allowedDirectSaleVariants: props.warehouse?.allowedDirectSaleVariants,
    // suppliers
    allSuppliers: defaultTo(false, props.warehouse?.allSuppliers),
    suppliers: props.warehouse?.suppliers,
    defaultSupplierId: props.warehouse?.defaultSupplierId,
  };

  return (
    <Form<WarehouseDetailFormType>
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      experimentalZodSchema={WarehouseDetailFormSchema}
      isDisabled={props.isEditDisabled}
      shouldWatchForUnsavedChanges
    >
      {(control, formApi, navigateWithoutBlocker) => {
        const handleAuthorizationProfileChange = (
          newAuthorizationProfileId: string | number | null
        ) => {
          if (isNil(newAuthorizationProfileId)) {
            return;
          }

          getAuthorizationProfile({
            authorizationProfileId: newAuthorizationProfileId as string,
            'x-tenant': props.tenant?.id as string,
          })
            .unwrap()
            .then((authorizationProfile) => {
              formApi.setValue('branchId', authorizationProfile?.branchId, {
                shouldValidate: true,
              });
            });
        };

        return (
          <VStack spacing={4} data-testid={props['data-testid']}>
            <Show when={isDuplicate}>
              <Alert
                variant="error"
                title={i18n.t('entity.warehouse.labels.warehouseAlreadyExists')}
                data-testid={testIds.settings.warehousesDetail('duplicityAlert')}
              />
            </Show>

            <GeneralForm
              control={control}
              branches={props.branches}
              tenant={props.tenant}
              authorizationProfiles={props.authorizationProfiles}
              manufacturers={props.manufacturers}
              onAuthorizationProfileChange={handleAuthorizationProfileChange}
              isAuthorizationProfileFetching={isAuthorizationProfileFetching}
              data-testid={suffixTestId('general', props)}
            />

            <Grid columns={2}>
              <Separator />
            </Grid>

            <AccountingForm
              control={control}
              warehousePricing={props.warehousePricing}
              currencies={props.currencies}
              data-testid={suffixTestId('accounting', props)}
            />

            <Grid columns={2}>
              <Separator />
            </Grid>

            <PricingForm
              control={control}
              formApi={formApi}
              salesPriceCalculationOptions={props.salesPriceCalculationOptions}
              baseSourcePriceOptions={props.baseSourcePriceOptions}
              data-testid={suffixTestId('pricing', props)}
            />

            <Grid columns={2}>
              <Separator />
            </Grid>

            <DocumentsForm
              control={control}
              series={props.series}
              serviceOrderVariants={props.serviceOrderVariants}
              directSaleVariants={props.directSaleVariants}
              data-testid={suffixTestId('documents', props)}
            />

            <Grid columns={2}>
              <Separator />
            </Grid>

            <SuppliersForm
              control={control}
              formApi={formApi}
              suppliers={props.suppliers}
              data-testid={suffixTestId('suppliers', props)}
            />

            <Space vertical={18} />

            <SettingsFooter
              actions={[
                {
                  type: 'button',
                  variant: 'secondary',
                  title: isNil(props.warehouse?.id)
                    ? i18n.t('general.actions.discard')
                    : i18n.t('general.actions.discardChanges'),
                  onClick: () => navigateWithoutBlocker(settingsRoutes.warehousesList),
                  isLoading: isCreatingWarehouse || isPatchingWarehouse,
                  'data-testid': suffixTestId('actions.discard', props),
                },
                {
                  type: 'form-button',
                  control,
                  buttonType: 'submit',
                  variant: 'primary',
                  title: isNil(props.warehouse?.id)
                    ? i18n.t('general.actions.save')
                    : i18n.t('general.actions.saveChanges'),
                  isLoading: isCreatingWarehouse || isPatchingWarehouse,
                  'data-testid': suffixTestId('actions.submit', props),
                },
              ]}
            />
          </VStack>
        );
      }}
    </Form>
  );
}
